import React from 'react';
import { Loader2, MapPin } from 'lucide-react';
import UnserviceableLocation from './UnserviceableLocation';
import LocationDetectionFallback from './LocationDetectionFallback';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import { displayLabelFor } from '../utils/deliveryLocationLabels';
import { btnPrimary, btnSecondary, btnSm, focusRing } from '../styles/ui';

// Home-page reminder that availability depends on where the order goes.
// Renders nothing once the customer has confirmed a location the backend can
// serve; otherwise it says what is missing and opens the location dialog.
const LocationAvailabilityPrompt = ({ className = '' }) => {
  const {
    status,
    label,
    pincode,
    source,
    coordinates,
    addressId,
    confirmedByUser,
    isServiceable,
    serviceContextError,
    locationDetection,
    setDeliveryLocation,
    refreshServiceContext,
    openLocationPrompt,
  } = useDeliveryLocation();

  if (status === 'unknown') {
    // Startup detection still running: a quiet status, never a prompt.
    if (locationDetection?.status === 'detecting') {
      return (
        <div
          role="status"
          data-testid="location-availability-prompt"
          data-state="detecting"
          className={`flex items-center gap-3 rounded-2xl border border-emerald-100 bg-white/90 px-4 py-3.5 shadow-soft text-sm text-emerald-900 ${className}`}
        >
          <Loader2 size={18} className="animate-spin shrink-0" aria-hidden="true" />
          Checking delivery availability…
        </div>
      );
    }
    return <LocationDetectionFallback className={className} testId="location-availability-prompt" />;
  }

  if (status === 'set' && isServiceable === false) {
    return <UnserviceableLocation className={className} />;
  }

  if (status === 'set' && serviceContextError) {
    return (
      <div
        role="alert"
        data-testid="location-availability-prompt"
        className={`rounded-2xl border border-red-100 bg-red-50 px-4 py-3.5 text-sm text-red-800 ${className}`}
      >
        We couldn&apos;t check delivery for {displayLabelFor({ label, pincode })}.
        <span className="flex gap-2 mt-2.5">
          <button type="button" onClick={refreshServiceContext} className={`${btnSecondary} ${btnSm} ${focusRing}`}>
            Try again
          </button>
          <button type="button" onClick={openLocationPrompt} className={`${btnSecondary} ${btnSm} ${focusRing}`}>
            Change location
          </button>
        </span>
      </div>
    );
  }

  // Seeded from the account's default address rather than chosen here.
  if (!confirmedByUser) {
    return (
      <div
        data-testid="location-availability-prompt"
        className={`flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-100 bg-white/90 px-4 py-3.5 shadow-soft ${className}`}
      >
        <span className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
          <MapPin size={18} aria-hidden="true" />
        </span>
        <p className="min-w-0 flex-1 text-sm text-ink-secondary">
          Showing availability for{' '}
          <span className="font-bold text-emerald-950 break-words">{displayLabelFor({ label, pincode })}</span>
        </p>
        <span className="flex gap-2">
          <button
            type="button"
            onClick={() => setDeliveryLocation({ pincode, coordinates, source, label, addressId, confirmedByUser: true })}
            className={`${btnPrimary} ${btnSm} ${focusRing}`}
          >
            Use this location
          </button>
          <button type="button" onClick={openLocationPrompt} className={`${btnSecondary} ${btnSm} ${focusRing}`}>
            Change
          </button>
        </span>
      </div>
    );
  }

  return null;
};

export default LocationAvailabilityPrompt;
